import { useState, useEffect, useRef, useCallback, lazy, Suspense } from "react";
import "./style.css";
import { PapelPicado, Calavera, Murcielagos, Petalos, Ofrenda, CALAVERITA, ArcoFlores, Retrato } from "./temporada";
import { COT_TIPOS, COT_GRUPOS, COT_MEDIOS, COT_PLAZOS, EN_LINEA, pesos, fmtDur, calcular } from "./tarifas";
import { t, tl, dinero, enIngles, enPesos, getIdioma, getMoneda, setIdioma, setMoneda, cargarTasas, MONEDAS } from "./i18n";
import { CALAVERITA_EN } from "./en";
import { SERVICIOS_SEO, PREGUNTAS, desde, valores, llenar } from "./servicios";
import { NEGOCIO, NEGOCIO_COMPLETO } from "./negocio";

// Pedidos, términos y aviso de privacidad se cargan solo cuando se abren (la portada carga menos código)
const Portal = lazy(() => import("./pedidos").then((m) => ({ default: m.Portal })));
const AvisoPrivacidad = lazy(() => import("./legal").then((m) => ({ default: m.AvisoPrivacidad })));
const Terminos = lazy(() => import("./legal").then((m) => ({ default: m.Terminos })));

// ═══════════════════════════════════════════════════════════════════
//  CONFIGURACIÓN — LO ÚNICO QUE NORMALMENTE HAY QUE EDITAR
// ═══════════════════════════════════════════════════════════════════
const CFG = {
  email: NEGOCIO.correo,   // nombre legal, domicilio, teléfono y horario van en src/negocio.js
  web3forms: "af69c63a-8415-4583-b6db-b77a7039aace",   // Access Key pública de web3forms.com (no cambiar)
  // Número de WhatsApp con lada de país, sin espacios ni "+". Ejemplo: "5215512345678".
  // Mientras esté vacío (así lo quiere Jamez por ahora), los botones llevan al formulario y el contacto directo es Instagram.
  whatsapp: "",
  instagram: "nietzschestudiosss",
  patreon: "https://www.patreon.com/cw/NietzscheFILMS",
  // Video de fondo del recuadro principal (opcional). Pon el archivo en public/trabajo/ y escribe "/trabajo/hero.mp4".
  // Que pese menos de 4 MB: 10 a 15 s, 1280 px de ancho, sin audio.
  heroVideo: "",
  heroPoster: "",
  estreno: "2027-05-01T00:00:00",   // fecha de "Algo viene"
  // Las solicitudes también se guardan en el Supabase del HQ (proyecto claqueta), además del correo de Web3Forms.
  // Es la llave PÚBLICA: con ella la página solo puede agregar solicitudes, nunca leer nada (sql/solicitudes-web.sql en el HQ).
  // Pedidos en línea (Google + Stripe). Apagado hasta que estén Stripe, Google y el SQL (supabase/LEEME.md).
  // Aunque lo pongas en true, no se prende mientras falten titular, domicilio o teléfono en src/negocio.js (lo pide la ley).
  // Mientras esté en false, "Mis pedidos" no aparece en la página pública; en tu compu se ve con ?demo=pedidos
  pedidosEnLinea: false,
  // Llave PUBLICABLE de Stripe (pk_test_… o pk_live_…). Esta sí puede ir aquí: es pública por diseño.
  // La Secret key (sk_…) NUNCA va en el código ni en un chat: va solo en los secretos de Supabase.
  // Llave PÚBLICA de Stripe en modo prueba (pk_test). Al pasar a cobros reales cámbiala por la pk_live. La secreta nunca va aquí.
  stripe: { llavePublica: "pk_test_51USQEkIS9u7rYVTRSFoVcy31BHN1gN4pk2YJsUrgs238AnllQH2eFOgXeK5Qdq9WbKMH2qn33Q6DSDlLMMR8zLUw0077A8QfHk" },
  supabase: {
    url: "https://ouigbpfyggjbaswyudyu.supabase.co",
    llavePublica: "sb_publishable_oAKwkn94Cp-IP-Qo35jD0g_1qOw4800",
  },
};

// Para probar en tu compu con el Supabase falso del HQ: ?sb=http://127.0.0.1:4545 en la dirección
const SB_URL = (() => {
  try {
    const q = new URLSearchParams(window.location.search).get("sb");
    if (q && /^http:\/\/(127\.0\.0\.1|localhost):\d+$/.test(q) && /^(127\.0\.0\.1|localhost)$/.test(window.location.hostname)) return q;
  } catch {}
  return CFG.supabase.url;
})();

// Los espacios vacíos de portafolio y testimonios están ocultos. Cambia a true para verlos y saber qué falta.
const MOSTRAR_VACIOS = false;
// En tu compu: ?demo=pedidos (datos de ejemplo, sin Stripe) o ?pedidos=real (Google, Supabase y Stripe de verdad, en modo prueba)
const PEDIDOS = (CFG.pedidosEnLinea && NEGOCIO_COMPLETO) || (() => { try {
  if (!/^(127\.0\.0\.1|localhost)$/.test(window.location.hostname)) return false;
  if (new URLSearchParams(window.location.search).get("pedidos") === "real") { sessionStorage.setItem("ns-real", "1"); sessionStorage.removeItem("ns-demo"); }
  return new URLSearchParams(window.location.search).get("demo") === "pedidos" || sessionStorage.getItem("ns-demo") === "1" || sessionStorage.getItem("ns-real") === "1";
} catch { return false; } })();
const conContenido = (p) => !!(p.video || p.poster || p.link);

// ─── TEMPORADAS ───
// La página se "viste" sola por fecha (hora de CDMX) y se quita sola. Para verla fuera de fecha: ?temporada=halloween
// (o ?temporada=ninguna para apagarla). Fechas MM-DD, inclusivas.
const TEMPORADAS = [
  { id: "halloween", desde: "10-15", hasta: "11-02",
    etiqueta: "Función de medianoche",
    cinta: ["Noche de brujas", "Día de Muertos", "Spots de temporada", "Contenido de terror", "Cine"] },
];
const TEMPORADA = (() => {
  try {
    const q = new URLSearchParams(window.location.search).get("temporada");
    if (q) return TEMPORADAS.find((t) => t.id === q) || null;
    const md = new Intl.DateTimeFormat("en-CA", { timeZone: "America/Mexico_City", month: "2-digit", day: "2-digit" })
      .format(new Date()).replace("/", "-");
    return TEMPORADAS.find((t) => (t.desde <= t.hasta ? md >= t.desde && md <= t.hasta : md >= t.desde || md <= t.hasta)) || null;
  } catch { return null; }
})();

const MUERTOS = TEMPORADA?.id === "halloween"; // arte de Día de Muertos y Halloween

const waLink = (texto) =>
  CFG.whatsapp ? `https://wa.me/${CFG.whatsapp}?text=${encodeURIComponent(texto)}` : "#contacto";
const waProps = (texto) =>
  CFG.whatsapp ? { href: waLink(texto), target: "_blank", rel: "noopener noreferrer" } : { href: "#contacto" };
const CTA_WA = CFG.whatsapp ? "Cotizar por WhatsApp" : "Cotiza tu proyecto";

// ─── LO QUE HACEMOS (textos de Doxa) ───
const SERVICIOS = [
  {
    icono: "camara",
    nombre: "Videografía comercial",
    gancho: "Anuncios de calidad incomparable.",
    texto: "Contrata a una productora de cine, no al nuevo tiktoker. La diferencia entre un anuncio que se ve caro y uno que se ve improvisado no es el presupuesto: es la habilidad de comunicar a tu audiencia.",
    incluye: ["Idea y concepto", "Producción completa", "Acabado profesional", "Una versión para cada plataforma"],
  },
  {
    icono: "reel",
    nombre: "Contenido para redes",
    gancho: "Reels con estándar de comercial.",
    texto: "Reels y TikToks filmados con cámara de cine y editados a mano, con el mismo cuidado que un spot. Tu marca se ve como tu marca, no como la tendencia de la semana.",
    incluye: ["Piezas sueltas o mensuales", "Visitas de grabación", "Subtítulos y portadas", "Cobertura de eventos"],
  },
  {
    icono: "diafragma",
    nombre: "Fotografía comercial",
    gancho: "Si la foto se ve barata, el producto se ve barato.",
    texto: "Es lo primero que ve tu cliente y muchas veces lo único. Una de nuestras imágenes no necesita descripción: se vende sola.",
    incluye: ["Producto, catálogo y campaña", "Dirección de arte", "Retoque profesional", "Listas para e-commerce y redes"],
  },
  {
    icono: "cubo",
    nombre: "Animación y modelado 3D",
    gancho: "Lo que ninguna cámara puede filmar.",
    texto: "Tu producto girando, abriéndose por dentro, flotando. Sin renta de locación, sin límite físico, sin volver a montar el set cada que cambias un detalle. Modelado a mano, cuadro por cuadro.",
    incluye: ["Modelado de producto", "Render fotorrealista", "Simulación y partículas", "Integrado con material filmado"],
  },
];



// ─── PORTAFOLIO ───
// Para agregar una pieza: pon el video (mp4 de 720 px, sin audio, menos de 3 MB) y su portada (jpg)
// en public/trabajo/ y agrégala aquí. Cada tipo arma su propia repisa en la videoteca, en el orden de esta lista.
// temporada: solo se muestra en esa temporada (ver TEMPORADAS). Todo es trabajo de Jamez (3D en Blender y videomontaje).
const TRABAJO = [
  // Repisa Animación 3D: de lo más impresionante a lo más sencillo; las cuatro peceras juntas como serie
  { titulo: "Dispensador de agua", lomo: "Dispensador", cliente: "Top Mart", tipo: "3D", video: "/trabajo/dispensador.mp4", poster: "/trabajo/dispensador.jpg" },
  { titulo: "Matamoscos", cliente: "Top Mart", tipo: "3D", video: "/trabajo/matamoscos.mp4", poster: "/trabajo/matamoscos.jpg" },
  { titulo: "Libreta panda", lomo: "Panda", cliente: "Top Mart", tipo: "3D", video: "/trabajo/libreta-panda.mp4", poster: "/trabajo/libreta-panda.jpg" },
  { titulo: "Agua", cliente: "Top Mart", tipo: "3D", video: "/trabajo/peceras-agua.mp4", poster: "/trabajo/peceras-agua.jpg" },
  { titulo: "Fuego", cliente: "Top Mart", tipo: "3D", video: "/trabajo/peceras-fuego.mp4", poster: "/trabajo/peceras-fuego.jpg" },
  { titulo: "Tierra", cliente: "Top Mart", tipo: "3D", video: "/trabajo/peceras-tierra.mp4", poster: "/trabajo/peceras-tierra.jpg" },
  { titulo: "Aire", cliente: "Top Mart", tipo: "3D", video: "/trabajo/peceras-aire.mp4", poster: "/trabajo/peceras-aire.jpg" },
  { titulo: "Maquillaje", cliente: "Top Mart", tipo: "3D", video: "/trabajo/maquillaje.mp4", poster: "/trabajo/maquillaje.jpg" },
  { titulo: "Rasuradora", cliente: "Top Mart", tipo: "3D", video: "/trabajo/rasuradora.mp4", poster: "/trabajo/rasuradora.jpg" },
  { titulo: "Carritos", cliente: "Top Mart", tipo: "3D", video: "/trabajo/carritos.mp4", poster: "/trabajo/carritos.jpg" },
  { titulo: "Memoria", cliente: "Top Mart", tipo: "3D", video: "/trabajo/memoria.mp4", poster: "/trabajo/memoria.jpg" },
  { titulo: "Peluche de la semana", lomo: "Peluche", cliente: "Top Mart", tipo: "3D", video: "/trabajo/peluche.mp4", poster: "/trabajo/peluche.jpg" },
  { titulo: "Vasos de brujería", lomo: "Brujería", cliente: "Top Mart", tipo: "3D", video: "/trabajo/vasos-brujeria.mp4", poster: "/trabajo/vasos-brujeria.jpg" },
  // Repisa Reel o TikTok: todo lo filmado con la FX30
  { titulo: "Showcase", cliente: "Top Mart", tipo: "Reel o TikTok", video: "/trabajo/showcase.mp4", poster: "/trabajo/showcase.jpg" },
  { titulo: "Caja ASMR", lomo: "Caja ASMR", cliente: "Top Mart", tipo: "Reel o TikTok", video: "/trabajo/caja-asmr.mp4", poster: "/trabajo/caja-asmr.jpg" },
  { titulo: "Lámparas", cliente: "Top Mart", tipo: "Reel o TikTok", video: "/trabajo/lamparas.mp4", poster: "/trabajo/lamparas.jpg" },
  { titulo: "Pirámide de platos", lomo: "Platos", cliente: "Top Mart", tipo: "Reel o TikTok", video: "/trabajo/piramide-platos.mp4", poster: "/trabajo/piramide-platos.jpg" },
  { titulo: "Bocina", cliente: "Top Mart", tipo: "Reel o TikTok", video: "/trabajo/gaby-bocina.mp4", poster: "/trabajo/gaby-bocina.jpg" },
  { titulo: "Tutorial de lámpara", lomo: "Tutorial", cliente: "Top Mart", tipo: "Reel o TikTok", video: "/trabajo/tutorial-lampara.mp4", poster: "/trabajo/tutorial-lampara.jpg" },
  { titulo: "Producto del Mundial", lomo: "Mundial", cliente: "Top Mart", tipo: "Reel o TikTok", video: "/trabajo/producto-mundial.mp4", poster: "/trabajo/producto-mundial.jpg" },
  { titulo: "Vasos de primavera", lomo: "Primavera", cliente: "Top Mart", tipo: "Reel o TikTok", video: "/trabajo/vasos-primavera.mp4", poster: "/trabajo/vasos-primavera.jpg" },
  { titulo: "Cojines", cliente: "Top Mart", tipo: "Reel o TikTok", video: "/trabajo/cojines.mp4", poster: "/trabajo/cojines.jpg" },
  { titulo: "Con todo a la cancha", lomo: "Cancha", cliente: "Top Mart", tipo: "Reel o TikTok", video: "/trabajo/con-todo-cancha.mp4", poster: "/trabajo/con-todo-cancha.jpg" },
  { titulo: "Ambiente sendero", lomo: "Sendero", cliente: "Top Mart", tipo: "Reel o TikTok", video: "/trabajo/ambiente-sendero.mp4", poster: "/trabajo/ambiente-sendero.jpg" },
  // Repisa Videomontaje: filmado más montaje y efectos
  { titulo: "Florero", cliente: "Top Mart", tipo: "Videomontaje", video: "/trabajo/florero.mp4", poster: "/trabajo/florero.jpg" },
  { titulo: "Capibara", cliente: "Top Mart", tipo: "Videomontaje", video: "/trabajo/capibara.mp4", poster: "/trabajo/capibara.jpg" },
  { titulo: "Lluvia de producto", lomo: "Lluvia", cliente: "Top Mart", tipo: "Videomontaje", video: "/trabajo/lluvia-producto.mp4", poster: "/trabajo/lluvia-producto.jpg" },
  { titulo: "Pizarrón mágico", lomo: "Pizarrón", cliente: "Top Mart", tipo: "Videomontaje", video: "/trabajo/pizarron.mp4", poster: "/trabajo/pizarron.jpg" },
  { titulo: "Calaverita", cliente: "Top Mart", tipo: "Videomontaje", video: "/trabajo/calaverita.mp4", poster: "/trabajo/calaverita.jpg", temporada: "halloween" },
  // Repisa Anuncio de TV
  { titulo: "Pausa de hidratación", lomo: "Hidratación", cliente: "Top Mart", tipo: "Anuncio de TV", video: "/trabajo/futbol-hidratacion.mp4", poster: "/trabajo/futbol-hidratacion.jpg" },
].filter((p) => !p.temporada || p.temporada === TEMPORADA?.id);

// ─── TESTIMONIOS ───
// Solo testimonios reales, con permiso del cliente. Ejemplo:
// { texto: "…", nombre: "Nombre Apellido", negocio: "Negocio, colonia" }
const TESTIMONIOS = [];

// Si todavía no hay ninguna pieza, la sección Trabajo y su liga del menú no aparecen
const HAY_TRABAJO = MOSTRAR_VACIOS || TRABAJO.some(conContenido);

// ─── FOTOGRAFÍA (public/fotos, 1600 px; las de vasos verano y entrevista con edición de color nueva) ───
// Las miniaturas de la cuadrícula (480 px, en public/fotos/mini) pesan una cuarta parte; al abrir una foto se ve la original.
// Si agregas una foto, haz su miniatura: ffmpeg -i foto.jpg -vf scale=480:-2 -q:v 4 mini/foto.jpg
const mini = (s) => s.replace("/fotos/", "/fotos/mini/");
const FOTOS = [
  { src: "/fotos/vasos-secuencia-009.jpg", alt: "Vaso rosa levantado contra el cielo" },
  { src: "/fotos/lamparas-016.jpg", alt: "Lámpara de cerdito encendida en la penumbra" },
  { src: "/fotos/vasos-secuencia-014.jpg", alt: "Modelo sosteniendo un vaso rosa de cristal" },
  { src: "/fotos/fotos-topmart-003.jpg", alt: "Clienta tomando de un vaso rosa en la tienda" },
  { src: "/fotos/vasos-secuencia-015.jpg", alt: "Vasos ámbar de burbujas sobre mesa naranja" },
  { src: "/fotos/lamparas-033.jpg", alt: "Lámpara blanca junto a una maceta" },
  { src: "/fotos/vasos-verano-008.jpg", alt: "Vaso rosa con popote entre hojas" },
  { src: "/fotos/vasos-secuencia-016.jpg", alt: "Vaso de burbujas color vino a contraluz" },
  { src: "/fotos/lamparas-021.jpg", alt: "Lámpara de cerdito con flores al fondo" },
  { src: "/fotos/fotos-topmart-002.jpg", alt: "Juguetes amarillos colgados en exhibidor" },
  { src: "/fotos/vasos-verano-011.jpg", alt: "Vaso verde estriado entre hojas" },
  { src: "/fotos/entrevista-005.jpg", alt: "Termo con tapa de panda en la mano" },
  { src: "/fotos/vasos-secuencia-008.jpg", alt: "Brindis con dos tarros de vidrio" },
  { src: "/fotos/lamparas-026.jpg", alt: "Lámpara encendida de cerca" },
  { src: "/fotos/entrevista-020.jpg", alt: "Sacapuntas de panda en la palma" },
  { src: "/fotos/vasos-verano-021.jpg", alt: "Tarro con jugo de naranja al sol" },
  { src: "/fotos/fotos-topmart-001.jpg", alt: "Accesorios de belleza en exhibidor" },
  { src: "/fotos/entrevista-029.jpg", alt: "Pluma de panda de varios colores" },
];

// ─── COLOR GRADING: stills de cortos que Jamez filmó y coloreó ───
const GRADING = [
  // por película y, dentro de cada una, en el orden en que aparecen
  { src: "/fotos/stills-birds-85.jpg", film: "By the Birds" },
  { src: "/fotos/stills-birds-340.jpg", film: "By the Birds" },
  { src: "/fotos/stills-birds-400.jpg", film: "By the Birds" },
  { src: "/fotos/stills-birds-580.jpg", film: "By the Birds" },
  { src: "/fotos/stills-birds-715.jpg", film: "By the Birds" },
  { src: "/fotos/stills-birds-850.jpg", film: "By the Birds" },
  { src: "/fotos/stills-birds-940.jpg", film: "By the Birds" },
  { src: "/fotos/stills-birds-1000.jpg", film: "By the Birds" },
  { src: "/fotos/stills-btb-003.jpg", film: "Burn the Bra" },
  { src: "/fotos/stills-btb-006.jpg", film: "Burn the Bra" },
  { src: "/fotos/stills-btb-008.jpg", film: "Burn the Bra" },
  { src: "/fotos/stills-btb-010.jpg", film: "Burn the Bra" },
  { src: "/fotos/stills-666-002.jpg", film: "APT 666" },
  { src: "/fotos/stills-666-004.jpg", film: "APT 666" },
  { src: "/fotos/stills-666-006.jpg", film: "APT 666" },
  { src: "/fotos/stills-666-008.jpg", film: "APT 666" },
];

// ─── CINE PROPIO ───
const FILMS = [
  { titulo: "No soy un héroe", estado: "En desarrollo", desc: "Una confrontación cruda con el mito del heroísmo en el México moderno." },
  { titulo: "Revolución Mexicana 2060", estado: "Preproducción", desc: "El futuro es revolución. El pasado es prólogo." },
];

const NIVELES = [
  { n: "El que pasa", d: "Imágenes sin contexto. Una señal por semana. Nada más.", p: "$3" },
  { n: "El que nota", d: "Señales y fragmentos de audio y texto sin identificar.", p: "$8" },
  { n: "El que se queda", d: "Todo lo anterior y detrás de cámaras del proceso real.", p: "$15" },
  { n: "El que entra", d: "Todo lo anterior, tu nombre en créditos y acceso dos semanas antes del estreno.", p: "$25" },
  { n: "El que actúa", d: "Todo lo anterior, playera de edición limitada, invitación a set y salir de extra.", p: "$100" },
  { n: "El que construye", d: "Todo lo anterior, crédito como productor asociado y voz en las decisiones creativas.", p: "$500", top: true },
];

// ═══════════════════════════════════════════════════════════════════
//  ICONOS PROPIOS (trazo de línea, sin emojis)
// ═══════════════════════════════════════════════════════════════════
const Ouroboros = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" className="ouro">
    <path d="M21.4 8.58 A10 10 0 1 1 15.42 2.6" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <circle cx="19.07" cy="4.93" r="1.7" fill="#FF0000" />
  </svg>
);

const Icono = ({ tipo, size = 32 }) => {
  const p = { fill: "none", stroke: "currentColor", strokeWidth: 1.4, strokeLinecap: "round", strokeLinejoin: "round" };
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      {tipo === "camara" && (<>
        <rect x="3" y="9" width="19" height="15" {...p} />
        <circle cx="12.5" cy="16.5" r="4.2" {...p} />
        <path d="M22 13.5 L29 10 V23 L22 19.5" {...p} />
        <circle cx="6.5" cy="12" r="0.9" fill="#FF0000" />
      </>)}
      {tipo === "reel" && (<>
        <rect x="9" y="3" width="14" height="26" {...p} />
        <path d="M14 12.5 L19.5 16 L14 19.5 Z" {...p} />
        <circle cx="19.8" cy="6.2" r="0.9" fill="#FF0000" />
      </>)}
      {tipo === "diafragma" && (<>
        <circle cx="16" cy="16" r="12" {...p} />
        <path d="M16 4 L21 13 M28 16 L18 17.5 M22 26.4 L16 19 M10 26.4 L13.5 17 M4 16 L13 14 M10 5.6 L16 13" {...p} />
        <circle cx="24.5" cy="7.5" r="0.9" fill="#FF0000" />
      </>)}
      {tipo === "cubo" && (<>
        <path d="M16 3 L28 9.5 V22.5 L16 29 L4 22.5 V9.5 Z" {...p} />
        <path d="M4 9.5 L16 16 L28 9.5 M16 16 V29" {...p} />
        <circle cx="16" cy="3" r="0.9" fill="#FF0000" />
      </>)}
      {tipo === "chat" && (<>
        <path d="M5 6 H27 V21 H14 L8 26 V21 H5 Z" {...p} />
        <path d="M10 12 H22 M10 16 H18" {...p} />
      </>)}
      {tipo === "play" && <path d="M11 7 L25 16 L11 25 Z" {...p} />}
      {tipo === "flecha-izq" && <path d="M20 7 L11 16 L20 25" {...p} strokeWidth="2" />}
      {tipo === "flecha-der" && <path d="M12 7 L21 16 L12 25" {...p} strokeWidth="2" />}
      {tipo === "salida" && <path d="M13 6 H26 V19 M26 6 L8 24" {...p} />}
      {tipo === "candado" && (<>
        <rect x="8" y="14" width="16" height="13" {...p} />
        <path d="M11 14 V10 A5 5 0 0 1 21 10 V14" {...p} />
      </>)}
    </svg>
  );
};

// ═══════════════════════════════════════════════════════════════════
//  PIEZAS
// ═══════════════════════════════════════════════════════════════════
const R = ({ children }) => <span className="rojo">{children}</span>;

const Ceja = ({ children }) => (
  <div className="ceja"><span className="ceja-guion" />{children}</div>
);

// Revelado al hacer scroll (solo en titulares y bloques grandes)
function Rv({ children, delay = 0, as: Tag = "div", className = "", ...rest }) {
  const [on, setOn] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (!("IntersectionObserver" in window) || window.matchMedia("(prefers-reduced-motion: reduce)").matches) { setOn(true); return; }
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setOn(true); io.disconnect(); }
    }, { threshold: 0.15, rootMargin: "0px 0px -40px 0px" });
    io.observe(node);
    return () => io.disconnect();
  }, []);
  return (
    <Tag ref={ref} className={`rv${on ? " on" : ""} ${className}`} style={{ transitionDelay: `${delay}ms` }} {...rest}>
      {/* el titular se revela por dentro; el contenedor queda visible para que el navegador lo detecte */}
      {className.includes("rv-titulo")
        ? <span className="rv-in" style={{ transitionDelay: `${delay}ms` }}>{children}</span>
        : children}
    </Tag>
  );
}

// Luces rojas en movimiento, como destellos de lente sobre el negro
const Luz = ({ variante = "a", proyector = false }) => (
  <div className={`luz luz-${variante}`} aria-hidden="true">
    <i className="luz-1" /><i className="luz-2" /><i className="destello" />
    {proyector && <i className="proyector" />}
  </div>
);

// Texto que se enciende palabra por palabra mientras bajas
const MANIFIESTO = [
  ["Nietzsche Studios es una productora audiovisual que existe para romper la agenda. Ponemos el "],
  ["arte y la cultura", true],
  [" por delante, con el hábito de la "],
  ["excelencia", true],
  [" en todo lo que producimos. Nos alejamos de lo genérico y de lo viral: buscamos que cada persona de nuestra audiencia sienta algo real. Todo lo hacemos a mano, "],
  ["por humanos", true],
  ["."],
];

const MANIFIESTO_EN = [
  ["Nietzsche Studios is an audiovisual production house that exists to break the agenda. We put "],
  ["art and culture", true],
  [" first, with the habit of "],
  ["excellence", true],
  [" in everything we produce. We steer away from the generic and the viral: we want every person in our audience to feel something real. Everything is made by hand, "],
  ["by humans", true],
  ["."],
];

function TextoEncendido({ partes, className }) {
  const ref = useRef(null);
  const palabras = partes.flatMap(([t, rojo]) =>
    t.split(/(\s+)/).filter(Boolean).map((w) => ({ w, rojo: !!rojo, espacio: /^\s+$/.test(w) })));
  const total = palabras.filter((p) => !p.espacio).length;
  const [n, setN] = useState(total);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let raf = 0;
    const calc = () => {
      raf = 0;
      const el = ref.current; if (!el) return;
      const r = el.getBoundingClientRect(), vh = window.innerHeight;
      const p = Math.min(1, Math.max(0, (vh * 0.85 - r.top) / (r.height + vh * 0.25)));
      setN(Math.round(p * total));
    };
    const on = () => { if (!raf) raf = requestAnimationFrame(calc); };
    calc();
    window.addEventListener("scroll", on, { passive: true });
    window.addEventListener("resize", on);
    return () => { window.removeEventListener("scroll", on); window.removeEventListener("resize", on); cancelAnimationFrame(raf); };
  }, [total]);

  let i = 0;
  return (
    <p ref={ref} className={`encendido ${className || ""}`}>
      {palabras.map((p, k) => {
        if (p.espacio) return p.w;
        const prendida = i++ < n;
        return <span key={k} className={`w${prendida ? " on" : ""}${p.rojo ? " rojo" : ""}`}>{p.w}</span>;
      })}
    </p>
  );
}

// Cinta de texto gigante que corre de lado a lado
function Marquesina({ frases, linea = false }) {
  const fila = (copia) => frases.flatMap((f, i) => [
    <span key={`${copia}f${i}`} className={linea && i % 2 ? "marq-hueco" : ""}>{t(f)}</span>,
    <Ouroboros key={`${copia}o${i}`} size={48} />,
  ]);
  return (
    <div className={`marquesina${linea ? " marquesina-linea" : ""}`} aria-hidden="true">
      <div className="marq-pista">{fila("a")}{fila("b")}</div>
    </div>
  );
}

const SET = [
  ["Super 35", "Sensor de cine Sony FX30"],
  ["4K", "Resolución de entrega"],
  ["10 bits", "Color con margen para corregir"],
  ["Rig completo", "Cámara armada para rodaje"],
];

function ElSet() {
  return (
    <section className="seccion seccion-orn seccion-set" id="set">
      <Luz variante="d" />
      <div className="contenedor">
        <Rv><Ceja>{t("El set")}</Ceja></Rv>
        <Rv as="h2" delay={80} className="titulo titulo-grande rv-titulo">{t("Equipo de cine, no de celular.")}</Rv>
        <ul className="set">
          {SET.map(([g, c], i) => (
            <Rv as="li" key={g} delay={i * 110}>
              <span className="set-grande">{t(g)}</span>
              <span className="set-chico">{t(c)}</span>
            </Rv>
          ))}
        </ul>
      </div>
    </section>
  );
}

// Código de tiempo como el monitor de la cámara (24 cuadros por segundo)
function useTimecode() {
  const [tc, setTc] = useState("00:00:00:00");
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const t0 = performance.now();
    let raf;
    const loop = (now) => {
      const f = Math.floor(((now - t0) / 1000) * 24);
      const ff = f % 24, s = Math.floor(f / 24) % 60, m = Math.floor(f / 1440) % 60, h = Math.floor(f / 86400);
      const z = (n) => String(n).padStart(2, "0");
      setTc(`${z(h)}:${z(m)}:${z(s)}:${z(ff)}`);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);
  return tc;
}

function Hero() {
  const tc = useTimecode();
  const visorRef = useRef(null);
  // Linterna de temporada: la luz sigue al cursor; en celular (o sin mouse) se pasea sola
  useEffect(() => {
    const v = visorRef.current;
    if (!TEMPORADA || !v) return;
    const quieto = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let raf = 0, t0 = performance.now(), mouse = false;
    const poner = (x, y) => { v.style.setProperty("--lx", `${x}px`); v.style.setProperty("--ly", `${y}px`); };
    const mover = (e) => { mouse = true; const r = v.getBoundingClientRect(); poner(e.clientX - r.left, e.clientY - r.top); };
    const pasear = (now) => {
      if (!mouse) { const r = v.getBoundingClientRect(), k = (now - t0) / 1000; poner(r.width * (0.5 + 0.32 * Math.sin(k * 0.55)), r.height * (0.5 + 0.22 * Math.sin(k * 0.9 + 1))); }
      raf = requestAnimationFrame(pasear);
    };
    const r = v.getBoundingClientRect(); poner(r.width * 0.5, r.height * 0.45);
    if (!quieto) raf = requestAnimationFrame(pasear);
    if (window.matchMedia("(pointer: fine)").matches) v.addEventListener("pointermove", mover);
    return () => { cancelAnimationFrame(raf); v.removeEventListener("pointermove", mover); };
  }, []);
  return (
    <header className="hero" id="inicio">
      {MUERTOS && <PapelPicado n={24} className="pp-hero" />}
      <div className="visor" ref={visorRef}>
        {MUERTOS && <Calavera className="cal-hero" />}
        {TEMPORADA && (<>
          <div className="linterna" aria-hidden="true" />
          <div className="rayones" aria-hidden="true"><i /><i /><i /></div>
        </>)}
        {CFG.heroVideo && (
          <video className="visor-video" src={CFG.heroVideo} poster={CFG.heroPoster || undefined}
            autoPlay muted loop playsInline preload="metadata" aria-hidden="true" />
        )}
        <div className="visor-tercios" aria-hidden="true" />
        <i className="destello visor-destello" aria-hidden="true" />
        <span className="esq esq-1" /><span className="esq esq-2" /><span className="esq esq-3" /><span className="esq esq-4" />

        <div className="visor-osd visor-arriba" aria-hidden="true">
          <span className="rec"><i />REC</span>
          <span className="tc">{tc}</span>
          {TEMPORADA && <span className="osd-temporada">{t(TEMPORADA.etiqueta)}</span>}
          <span className="osd-der">4K&nbsp;&nbsp;24P&nbsp;&nbsp;FX30</span>
        </div>

        <div className="hero-texto">
          <h1>{t("Que tu marca se vea de película.")}</h1>
          <p className="hero-sub">{t("Casa productora en la Ciudad de México. Spots, video de marca y contenido para redes, filmados con cámara de cine y editados a mano.")}</p>
          <div className="hero-botones">
            <a className="btn btn-rojo" {...waProps("Hola, vi su página y quiero cotizar.")}>
              <Icono tipo="chat" size={20} />{t(CTA_WA)}
            </a>
            {HAY_TRABAJO
              ? <a className="btn btn-linea" href="#trabajo">{t("Ver trabajo")}</a>
              : <a className="btn btn-linea" href="#doxa">{t("Ver servicios")}</a>}
          </div>
        </div>

        <div className="visor-osd visor-abajo" aria-hidden="true">
          <span>ISO 800</span><span>f/2.8</span><span>1/50</span><span className="osd-der">CDMX</span>
        </div>
      </div>

      <ul className="credo">
        <li>{t("Hecho a mano.")}</li>
        <li>{t("Por humanos.")}</li>
        <li>{t("Para humanos.")}</li>
      </ul>
    </header>
  );
}

function Manifiesto() {
  return (
    <section className="seccion seccion-orn seccion-manifiesto" id="manifiesto">
      <Luz variante="a" />
      <div className="contenedor manifiesto">
        <Rv><Ceja>{t("Manifiesto")}</Ceja></Rv>
        <Rv as="h2" delay={80} className="titulo titulo-mega rv-titulo">
          {enIngles() ? <>Giving the <R>soul</R> back to <R>art</R>.</> : <>Devolviéndole el <R>alma</R> al <R>arte</R>.</>}
        </Rv>
        <TextoEncendido key={getIdioma()} partes={enIngles() ? MANIFIESTO_EN : MANIFIESTO} className="manifiesto-texto" />
      </div>
    </section>
  );
}

const PILARES = [
  { href: "#doxa", nombre: "Doxa by Nietzsche", tag: "Producción comercial.", desc: "Video, fotografía y animación 3D para marcas. La rama que financia el arte. Hecho a mano, por humanos." },
  { href: "#films", nombre: "Nietzsche Films", tag: "Emociones intensas.", desc: "Cine que hace sentir. Historias que se quedan contigo mucho después de los créditos. Emoción real, hecha a mano." },
];

function Pilares() {
  return (
    <section className="seccion" id="pilares">
      <div className="contenedor">
        <Rv as="h2" className="titulo titulo-grande rv-titulo">{enIngles() ? <>Two pillars. One <R>vision</R>. Zero compromises.</> : <>Dos pilares. Una <R>visión</R>. Cero concesiones.</>}</Rv>
        <div className="pilares">
          {PILARES.map((p, i) => (
            <Rv key={p.nombre} delay={i * 120}>
              <a className="pilar" href={p.href}>
                <span className="pilar-tag">{t(p.tag)}</span>
                <span className="pilar-nombre">{p.nombre}</span>
                <span className="pilar-desc">{t(p.desc)}</span>
                <span className="pilar-entrar">{t("Entrar")}</span>
              </a>
            </Rv>
          ))}
        </div>
      </div>
    </section>
  );
}

function Doxa() {
  return (
    <section className="seccion seccion-orn seccion-doxa" id="doxa">
      <Luz variante="b" />
      <div className="contenedor">
        <div className="doxa-cabeza">
          <Rv><Ceja>Doxa by Nietzsche</Ceja></Rv>
          <Rv as="h2" delay={80} className="titulo titulo-mega rv-titulo">{t("Elevamos la calidad de tu producto.")}</Rv>
          <Rv as="p" delay={160} className="lead">{t("Anuncios que se ven como cine, no como contenido. Video, fotografía y animación 3D para marcas que ya se cansaron de verse igual que todos.")}</Rv>
        </div>

        <div className="servicios" id="servicios">
          {SERVICIOS.map((s) => (
            <article className="servicio" key={s.nombre}>
              <div className="servicio-cabeza">
                <div className="servicio-icono"><Icono tipo={s.icono} size={44} /></div>
                <p className="servicio-gancho">{t(s.gancho)}</p>
                <h3>{t(s.nombre)}</h3>
              </div>
              <div className="servicio-cuerpo">
                <p>{t(s.texto)}</p>
                <ul className="servicio-lista">
                  {s.incluye.map((x) => <li key={x}>{t(x)}</li>)}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── VIDEOTECA: el portafolio como cintas VHS en una repisa ───
// Elegir una cinta la saca de la repisa y la "mete" al monitor (con estática de VHS). Flechas para la anterior o la siguiente.
// El monitor se reproduce solo y sin sonido mientras está en pantalla; con "reducir movimiento" se toca para verlo.
const ETIQUETAS = ["hueso", "rojo", "negro"];
// Fecha estilo cámara de los 90 en la esquina del monitor (hoy, en CDMX)
const FECHA_VHS = (() => { try { return new Intl.DateTimeFormat("en-US", { timeZone: "America/Mexico_City", month: "short", day: "2-digit", year: "numeric" }).format(new Date()).toUpperCase().replace(",", ""); } catch { return ""; } })();
const tcVideo = (t) => { const f = Math.floor((t || 0) * 24), z = (n) => String(n).padStart(2, "0"); return `00:${z(Math.floor(f / 1440))}:${z(Math.floor(f / 24) % 60)}:${z(f % 24)}`; };

function Videoteca({ cintas }) {
  const [i, setI] = useState(0);
  const [estatica, setEstatica] = useState(false);
  const [pausado, setPausado] = useState(true);
  const [tc, setTc] = useState("00:00:00:00");
  const video = useRef(null), monitor = useRef(null), repisa = useRef(null), visible = useRef(false);
  const quieto = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const c = cintas[i];
  const n = (k) => String(k + 1).padStart(3, "0");

  const reproducir = () => { const v = video.current; if (v) v.play().then(() => setPausado(false)).catch(() => {}); };
  // reproducir solo mientras el monitor está en pantalla
  useEffect(() => {
    const m = monitor.current;
    if (!m || !("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver(([e]) => {
      visible.current = e.isIntersecting;
      if (e.isIntersecting && !quieto) reproducir(); else if (!e.isIntersecting) { video.current?.pause(); setPausado(true); }
    }, { threshold: 0.3 });
    io.observe(m); return () => io.disconnect();
  }, [quieto]);
  // al cambiar de cinta: estática breve y arranca la nueva
  useEffect(() => {
    if (quieto) return;
    setEstatica(true);
    const t = setTimeout(() => { setEstatica(false); if (visible.current) reproducir(); }, 380);
    return () => clearTimeout(t);
  }, [i, quieto]);
  const elegir = (k) => setI((k + cintas.length) % cintas.length);
  const mover = (d) => setI((cur) => (cur + d + cintas.length) % cintas.length);
  // la cinta elegida se acomoda a la vista dentro de la repisa (sin mover la página)
  useEffect(() => {
    const t = repisa.current?.querySelector(`.cinta[data-k="${i}"]`), r = t?.parentElement;
    if (r && t) r.scrollTo({ left: t.offsetLeft - r.clientWidth / 2 + t.clientWidth / 2, behavior: quieto ? "auto" : "smooth" });
  }, [i, quieto]);
  // una repisa por tipo, en el orden en que aparecen en TRABAJO
  const repisas = [];
  cintas.forEach((p, k) => {
    const nombre = p.tipo === "3D" ? "Animación 3D" : p.tipo;
    let g = repisas.find((x) => x.nombre === nombre);
    if (!g) repisas.push((g = { nombre, cintas: [] }));
    g.cintas.push([p, k]);
  });
  const alternar = () => { const v = video.current; if (!v) return; if (v.paused) reproducir(); else { v.pause(); setPausado(true); } };

  return (
    <div className="videoteca">
      <div className="monitor" ref={monitor}>
        <div className="monitor-pantalla">
          <video className="monitor-fondo" key={`f${c.video}`} src={c.video} muted loop playsInline preload="metadata" aria-hidden="true"
            poster={c.poster} />
          <video className="monitor-video" key={c.video} ref={video} src={c.video} poster={c.poster} muted loop playsInline preload="metadata"
            onClick={alternar} onTimeUpdate={(e) => setTc(tcVideo(e.currentTarget.currentTime))}
            onPlay={(e) => { const f = e.currentTarget.parentElement.querySelector(".monitor-fondo"); f && f.play().catch(() => {}); }}
            onPause={(e) => { const f = e.currentTarget.parentElement.querySelector(".monitor-fondo"); f && f.pause(); }}
            aria-label={`${t("CINTA")} ${n(i)}, ${t(c.titulo)}: ${t(c.tipo === "3D" ? "Animación 3D" : c.tipo)} · ${c.cliente}`} />
          <div className="monitor-lineas" aria-hidden="true" />
          <div className="monitor-tracking" aria-hidden="true" />
          <div className="monitor-vineta" aria-hidden="true" />
          {estatica && <div className="monitor-estatica" aria-hidden="true" />}
          <div className="monitor-osd monitor-arriba" aria-hidden="true">
            <span className={`osd-play${pausado ? " pausa" : ""}`}>{pausado ? t("PAUSA") : "PLAY"}</span>
            <span className="osd-tc">{tc}</span>
            <span className="osd-der">SP</span>
          </div>
          <div className="monitor-osd monitor-abajo" aria-hidden="true">
            <span>{t("CINTA")} {n(i)}</span><span>{c.tipo === "3D" ? "3D" : t(c.tipo).toUpperCase()}</span><span className="osd-der osd-fecha">{FECHA_VHS}</span>
          </div>
          {pausado && <button className="pieza-play monitor-play" onClick={alternar} aria-label={t("Reproducir")}><Icono tipo="play" size={26} /></button>}
        </div>
      </div>

      <div className="repisa-zona">
        <div className="repisa-control">
          <button className="flecha" onClick={() => mover(-1)} aria-label={t("Cinta anterior")}><Icono tipo="flecha-izq" size={22} /></button>
          <span className="repisa-cuenta">{n(i)} / {n(cintas.length - 1)}</span>
          <button className="flecha" onClick={() => mover(1)} aria-label={t("Cinta siguiente")}><Icono tipo="flecha-der" size={22} /></button>
        </div>
        <div className="repisas" ref={repisa}
          onKeyDown={(e) => { if (e.key === "ArrowRight") { e.preventDefault(); mover(1); } if (e.key === "ArrowLeft") { e.preventDefault(); mover(-1); } }}>
          {repisas.map((g) => (
            <div className="repisa-grupo" key={g.nombre}>
              <h3 className="repisa-nombre">{t(g.nombre)}<span>{g.cintas.length} {t(g.cintas.length === 1 ? "cinta" : "cintas")}</span></h3>
              <div className="repisa" role="listbox" aria-label={t(g.nombre)}>
                {g.cintas.map(([p, k], j) => (
                  <button key={p.video} data-k={k} role="option" aria-selected={k === i} className={`cinta cinta-${ETIQUETAS[k % ETIQUETAS.length]}${k === i ? " activa" : ""}${j === g.cintas.length - 1 && g.cintas.length > 2 ? " recargada" : ""}`}
                    onClick={() => elegir(k)} aria-label={`${t("CINTA")} ${n(k)}, ${t(p.titulo)}`}>
                    <span className="cinta-tope" />
                    <span className="cinta-etiqueta"><b>{t(p.lomo || p.titulo)}</b></span>
                    <span className="cinta-sello"><Ouroboros size={16} /></span>
                    <span className="cinta-vhs" aria-hidden="true">VHS</span>
                    <span className="cinta-anio" aria-hidden="true">{n(k)}</span>
                  </button>
                ))}
              </div>
              <div className="repisa-tabla" aria-hidden="true" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Trabajo() {
  const cintas = TRABAJO.filter(conContenido);
  if (!cintas.length) return null;
  return (
    <section className="seccion seccion-trabajo" id="trabajo">
      <div className="contenedor">
        <Rv><Ceja>{t("Trabajo")}</Ceja></Rv>
        <Rv as="h2" delay={80} className="titulo titulo-grande rv-titulo">{t("Marcas que dejaron de conformarse.")}</Rv>
        <p className="lead">{t("Animación 3D hecha a mano en Blender, reels filmados con cámara de cine, videomontaje y anuncios para TV. Elige una cinta.")}</p>
        <Videoteca cintas={cintas} />
        <p className="galeria-mas">
          {t("¿Dudas rápidas? Mándanos DM en Instagram:")}{" "}
          <a href={`https://ig.me/m/${CFG.instagram}`} target="_blank" rel="noopener noreferrer">@{CFG.instagram}</a>
        </p>
      </div>
    </section>
  );
}

// Cada servicio con su precio "desde" (el mismo cálculo del cotizador) y su página propia para Google
function ListaServicios() {
  const en = enIngles();
  return (
    <div className="lista-servicios">
      <h3 className="ls-titulo">{t("Qué incluye cada servicio")}</h3>
      <ul>
        {SERVICIOS_SEO.map((s) => {
          const d = desde(s.id);
          return (
            <li key={s.id}>
              <a className="ls-fila" href={en ? `/en/services/${s.slugEn}/` : `/servicios/${s.slug}/`}>
                <span className="ls-nombre">{en ? s.nombreEn : s.nombre}</span>
                <span className="ls-corto">{en ? s.cortoEn : s.corto}</span>
                <span className="ls-entrega">{t(d.entrega[0])}</span>
                <span className="ls-precio"><small>{t("desde")}</small> {dinero(d.monto)} <small>{t("+ IVA")} · {en ? d.piezaEn : d.pieza}</small><small className="ls-con-iva">{t("{total} con IVA", { total: dinero(d.total) })}</small></span>
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function Precios() {
  return (
    <section className="seccion" id="precios">
      <div className="contenedor">
        <Rv><Ceja>{t("Cómo cobramos")}</Ceja></Rv>
        <Rv as="h2" delay={80} className="titulo titulo-grande rv-titulo">{t("Transparencia pura.")}</Rv>
        <p className="lead">{t("Cada cotización lleva el desglose completo del precio: sabes exactamente qué está pagando cada peso. Aquí nadie te contesta “depende” y cuelga.")}</p>
        <Cotizador />
        <p className="nota">{enPesos() ? t("Precios en MXN más IVA; el total con IVA es lo que pagas, sin cargos extra por pagar con tarjeta. Lo que se filma o se fotografía aparta fecha con un anticipo del 50%; el 3D, la edición y el color se pagan completos al pedir.") : t("Precios aproximados en {moneda} con el tipo de cambio de hoy. Cobramos en pesos mexicanos (MXN); el total con IVA es lo que pagas, sin cargos extra por pagar con tarjeta. Lo que se filma o se fotografía aparta fecha con un anticipo del 50%; el 3D, la edición y el color se pagan completos al pedir.", { moneda: getMoneda() })}</p>
        <ListaServicios />

        <ol className="pasos" aria-label={t("Cómo trabajamos")}>
          {PASOS.map(([titulo, d], i) => (
            <li key={titulo}>
              <span className="paso-num">{String(i + 1).padStart(2, "0")}</span>
              <div><h3>{t(titulo)}</h3><p>{t(d)}</p></div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function Opciones({ nombre, valor, opciones, onCambio }) {
  return (
    <div className="cot-grupo" role="radiogroup" aria-label={nombre}>
      <span className="cot-etiqueta">{nombre}</span>
      <div className="cot-opciones">
        {opciones.map(([v, txt]) => (
          <button key={v} type="button" role="radio" aria-checked={valor === v}
            className={`cot-op${valor === v ? " on" : ""}`} onClick={() => onCambio(v)}>{txt}</button>
        ))}
      </div>
    </div>
  );
}

// Cuánto: fotos, duración, horas de evento o reels al mes, según el tipo
function cantidadDe(tt) {
  if (tt.fotos) return ["fotos", t("Cuántas fotos"), tt.fotos.map((n) => [n, t("{n} fotos", { n })])];
  if (tt.reels) return ["reels", t("Cuántos reels al mes"), tt.reels.map(([n]) => [n, t("{n} reels", { n })])];
  if (tt.horasEvento) return ["horas", t("Cuántas horas dura el evento"), tt.horasEvento.map((n) => [n, t("{n} h", { n })])];
  if (tt.dur) return ["dur", t("Duración"), tt.dur.map((d, i) => [d, i === 0 && tt.hasta ? t("Hasta {d}", { d: fmtDur(d) }) : fmtDur(d)])];
  return null;
}
const textoCantidad = (tt, c) => tt.fotos ? t("{n} fotos", { n: c.fotos }) : tt.reels ? t("{n} reels al mes", { n: c.reels }) : tt.horasEvento ? t("Evento de {n} h", { n: c.horas })
  : tt.dur ? t("Duración: {d}", { d: (tt.hasta && c.dur === tt.dur[0] ? t("hasta") + " " : "") + fmtDur(c.dur) }) : t("Modelo con 3 renders");

function Cotizador() {
  const [pequeno, setPequeno] = useState(false);
  const recibo = useRef(null);
  const abrirPequeno = () => { setPequeno(true); setTimeout(() => recibo.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 50); };
  const [c, setC] = useState({ tipo: "a3d", dur: 15, fotos: 10, horas: 4, reels: 4, versiones: 1, medio: "organico", plazo: "1a", urgente: false });
  const pon = (k) => (v) => setC((x) => {
    const n = { ...x, [k]: v };
    if (k === "tipo" && v !== "otro") {
      const tt = COT_TIPOS.find((y) => y.id === v);
      if (tt.dur && !tt.dur.includes(n.dur)) n.dur = tt.ref;
      if (tt.fotos && !tt.fotos.includes(n.fotos)) n.fotos = tt.fotos[0];
    }
    return n;
  });
  const otro = c.tipo === "otro";
  const r = otro ? null : calcular(c);
  const cant = r && cantidadDe(r.t);
  const avisar = (texto, tipo) => {
    window.dispatchEvent(new CustomEvent("cotizacion", { detail: { texto, tipo } }));
    document.getElementById("contacto")?.scrollIntoView({ behavior: "smooth" });
  };
  const enviar = () => avisar([
    t("Cotización estimada en el sitio: {tipo}", { tipo: t(r.t.nombre) }),
    textoCantidad(r.t, c),
    ...(r.t.sinVersiones ? [] : [t("Versiones o formatos: {n}", { n: c.versiones })]),
    ...(r.t.sinDerechos ? [] : [t("Uso: {uso}", { uso: `${t(COT_MEDIOS.find((m) => m.id === c.medio).nombre)}${c.medio === "organico" ? "" : ", " + t(COT_PLAZOS.find((p) => p.id === c.plazo).nombre)}` })]),
    t("Entrega: {e}", { e: t(r.t.entrega[c.urgente ? 1 : 0]) }),
    t("Estimado: {sub} + IVA = {total}", { sub: pesos(r.subtotal), total: pesos(r.total) }) + (enPesos() ? "" : ` (≈ ${dinero(r.total)})`),
    "", t("Mi proyecto: "),
  ].join("\n"), c.tipo);
  return (
    <div className="cotizador" id="cotizador">
      <div className="cot-panel">
        <h3 className="cot-titulo">{t("Arma tu presupuesto")}</h3>
        <p className="cot-sub">{t("Mueve las opciones y mira el precio al instante, con todo desglosado.")}</p>
        <div className="cot-grupo cot-tipos" role="radiogroup" aria-label={t("Qué necesitas")}>
          <span className="cot-etiqueta">{t("Qué necesitas")}</span>
          {COT_GRUPOS.map((g) => (
            <div className="cot-familia" key={g}>
              <span className="cot-familia-nombre">{t(g)}</span>
              <div className="cot-opciones">
                {COT_TIPOS.filter((x) => x.grupo === g).map((x) => (
                  <button key={x.id} type="button" role="radio" aria-checked={c.tipo === x.id} className={`cot-op${c.tipo === x.id ? " on" : ""}`} onClick={() => pon("tipo")(x.id)}>{t(x.nombre)}</button>
                ))}
              </div>
            </div>
          ))}
          <div className="cot-familia">
            <span className="cot-familia-nombre">{t("¿No está en la lista?")}</span>
            <div className="cot-opciones">
              <button type="button" role="radio" aria-checked={otro} className={`cot-op cot-op-otro${otro ? " on" : ""}`} onClick={() => pon("tipo")("otro")}>{t("Algo más")}</button>
            </div>
          </div>
        </div>
        {!otro && <>
          {cant && <Opciones nombre={cant[1]} valor={c[cant[0]]} onCambio={pon(cant[0])} opciones={cant[2]} />}
          {!r.t.sinVersiones && <Opciones nombre={t("Versiones o formatos")} valor={c.versiones} onCambio={pon("versiones")} opciones={[[1, "1"], [2, "2"], [3, "3"], [4, "4"]]} />}
          {!r.t.sinDerechos && <Opciones nombre={t("Dónde se va a usar")} valor={c.medio} onCambio={pon("medio")} opciones={COT_MEDIOS.map((m) => [m.id, t(m.nombre)])} />}
          {!r.t.sinDerechos && c.medio !== "organico" && <Opciones nombre={t("Por cuánto tiempo")} valor={c.plazo} onCambio={pon("plazo")} opciones={COT_PLAZOS.map((p) => [p.id, t(p.nombre)])} />}
          {r.t.sinUrgencia
            ? <div className="cot-grupo"><span className="cot-etiqueta">{t("Entrega")}</span><p className="cot-fijo">{t(r.t.entrega[0])}</p></div>
            : <Opciones nombre={t("Entrega")} valor={c.urgente} onCambio={pon("urgente")} opciones={[[false, t(r.t.entrega[0])], [true, t(r.t.entrega[1])]]} />}
        </>}
        <p className="cot-pequeno-liga"><button type="button" onClick={abrirPequeno} aria-expanded={pequeno}>{t("¿Eres una empresa o un artista pequeño?")}</button></p>
      </div>
      <div className="cot-lado" ref={recibo}>
      {pequeno ? <ProyectoPequeno volver={() => setPequeno(false)} /> : otro ? (
        <div className="cot-recibo cot-otro" aria-live="polite">
          <div className="cot-recibo-cab"><span>{t("Algo más")}</span><span /></div>
          <h4 className="cot-otro-titulo">{t("Cuéntanos qué necesitas.")}</h4>
          <p>{t("Un personaje en 3D, la animación de tu logo, un proyecto con varias piezas o algo que todavía no tiene nombre: escríbenos y te mandamos la cotización por escrito, con el mismo desglose, en menos de 24 horas.")}</p>
          <button type="button" className="btn btn-rojo btn-ancho" onClick={() => avisar(t("Necesito: "), "otro")}>{t("Cuéntanos qué necesitas")}</button>
          <p className="cot-letra">{t("También puedes mandarnos mensaje directo en Instagram.")}</p>
        </div>
      ) : (
      <div className="cot-recibo" aria-live="polite">
        <div className="cot-recibo-cab">
          <span>{t("Presupuesto estimado")}</span>
          <span>{r.horas ? t("{h} h de trabajo", { h: r.horas }) : ""}</span>
        </div>
        <ul className="cot-lineas">
          {r.lineas.map((l) => (
            <li key={l.n}><span>{tl(l.n)}{l.horas ? <em>{l.horas} h</em> : l.detalle ? <em>{t(l.detalle)}</em> : null}</span><span>{dinero(l.monto)}</span></li>
          ))}
          <li className="cot-sub-linea"><span>{t("Producción")}</span><span>{dinero(r.produccion)}</span></li>
          {r.extras.map((l) => (
            <li key={l.n}><span>{tl(l.n)}</span><span>{l.incluido ? t("Incluido") : dinero(l.monto)}</span></li>
          ))}
          <li><span>{t("Dos rondas de ajustes")}</span><span>{t("Incluido")}</span></li>
          <li className="cot-sub-linea"><span>{t("Subtotal")}</span><span>{dinero(r.subtotal)}</span></li>
          <li><span>{t("IVA 16%")}</span><span>{dinero(r.iva)}</span></li>
        </ul>
        <div className="cot-total"><span>{r.t.reels ? t("Total al mes") : t("Total")}</span><span className="monto">{dinero(r.total)}</span></div>
        {PEDIDOS && EN_LINEA.includes(c.tipo) ? (<>
          <p className="cot-anticipo">{c.tipo === "foto" ? t("Se pide en línea: 50% al pedir y 50% al recibir tus fotos.") : t("Se pide y se paga aquí mismo, sin esperar cotización.")}</p>
          <button type="button" className="btn btn-rojo btn-ancho" onClick={() => window.dispatchEvent(new CustomEvent("pedir", { detail: c }))}>{t("Pedir y pagar en línea")}</button>
          <button type="button" className="btn btn-linea btn-ancho cot-segundo" onClick={enviar}>{t("Prefiero platicarlo primero")}</button>
        </>) : (<>
          <p className="cot-anticipo">{r.t.reels ? t("Se paga mes por mes, al inicio de cada mes.") : r.t.anticipo ? t("Anticipo del 50% para apartar fecha: {monto}", { monto: dinero(r.total / 2) }) : t("Se paga completo al pedir, y arrancamos.")}</p>
          <button type="button" className="btn btn-rojo btn-ancho" onClick={enviar}>{t("Enviar este presupuesto")}</button>
        </>)}
        <p className="cot-letra">{t("Es un estimado. Te confirmamos el precio final por escrito en menos de 24 horas. Spots, videos de marca y cortos llevan música original; lo demás, música de librería con licencia. Actores y locaciones se cotizan aparte.")}</p>
      </div>
      )}
      </div>
    </div>
  );
}

const PASOS = [
  ["Nos escribes", "Por el formulario o por mensaje directo en Instagram. Cuéntanos qué vendes y qué necesitas."],
  ["Cotización en 24 horas", "Por escrito, con desglose y fecha de entrega. Sin juntas eternas."],
  ["Apartamos fecha", "Con el anticipo agendamos rodaje y preproducción."],
  ["Entregamos listo", "Con dos rondas de ajustes y una versión para cada red."],
];

function Testimonios() {
  const lista = TESTIMONIOS.length ? TESTIMONIOS : MOSTRAR_VACIOS ? [null, null] : [];
  if (!lista.length) return null;
  return (
    <section className="seccion" id="clientes">
      <div className="contenedor">
        <Ceja>{t("Clientes")}</Ceja>
        <h2 className="titulo">{t("Lo dicen ellos.")}</h2>
        <div className="testimonios">
          {lista.map((t, i) => t ? (
            <blockquote className="testimonio" key={i}>
              <p>{t.texto}</p>
              <footer><strong>{t.nombre}</strong>{t.negocio && <span>{t.negocio}</span>}</footer>
            </blockquote>
          ) : (
            <div className="testimonio testimonio-vacio" key={i}>
              <p>Espacio para un testimonio real: dos o tres líneas de un cliente contento, con su nombre y negocio.</p>
              <footer>Espacio vacío (MOSTRAR_VACIOS)</footer>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Foto o still abierto ahí mismo, arriba de las miniaturas (no a pantalla completa): flechas, teclado y cerrar
function Expandido({ lista, i, setI, pie, ancho = false }) {
  const caja = useRef(null);
  const n = lista.length;
  const pasar = useCallback((d) => setI((k) => (k + d + n) % n), [n, setI]);
  useEffect(() => {
    if (i === null) return;
    const tecla = (e) => {
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName)) return;
      if (e.key === "Escape") setI(null);
      if (e.key === "ArrowRight") pasar(1);
      if (e.key === "ArrowLeft") pasar(-1);
    };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, [i, pasar, setI]);
  // al abrir, se acomoda a la vista sin brincar toda la página
  const abierto = i !== null;
  useEffect(() => { if (abierto) caja.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [abierto]);
  if (!abierto) return null;
  const f = lista[i];
  return (
    <figure className={`expandido${ancho ? " expandido-ancho" : ""}`} ref={caja} aria-live="polite">
      <div className="expandido-marco">
        <img key={f.src} src={f.src} alt={f.alt || pie(f)} />
        <button className="flecha expandido-ant" onClick={() => pasar(-1)} aria-label={t("Anterior")}><Icono tipo="flecha-izq" size={22} /></button>
        <button className="flecha expandido-sig" onClick={() => pasar(1)} aria-label={t("Siguiente")}><Icono tipo="flecha-der" size={22} /></button>
      </div>
      <figcaption>
        <span>{pie(f)}</span>
        <span className="expandido-cuenta">{String(i + 1).padStart(2, "0")} / {String(n).padStart(2, "0")}</span>
        <button className="expandido-cerrar" onClick={() => setI(null)}>{t("Cerrar")}</button>
      </figcaption>
    </figure>
  );
}

function Fotografia() {
  const [i, setI] = useState(null);
  if (!FOTOS.length) return null;
  return (
    <section className="seccion seccion-fotos" id="fotografia">
      <div className="contenedor">
        <Rv><Ceja>{t("Fotografía")}</Ceja></Rv>
        <Rv as="h2" delay={80} className="titulo titulo-grande rv-titulo">{t("Producto con luz de cine.")}</Rv>
        <p className="lead">{t("Sesiones de producto en estudio, en exterior y en tienda. Iluminadas, dirigidas y editadas a mano.")}</p>
        <Expandido lista={FOTOS} i={i} setI={setI} pie={(f) => t(f.alt)} />
        <div className="fotos">
          {FOTOS.map((f, k) => (
            <button key={f.src} className={`foto${k === i ? " activa" : ""}`} onClick={() => setI(k === i ? null : k)}
              aria-label={`${t("Ver foto")}: ${t(f.alt)}`} aria-pressed={k === i}>
              <img src={mini(f.src)} alt={t(f.alt)} loading="lazy" />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

function ColorGrading() {
  const [i, setI] = useState(null);
  const tira = useRef(null);
  // el still elegido se acomoda a la vista dentro de la tira
  useEffect(() => {
    const t = tira.current?.children[i];
    if (t) tira.current.scrollTo({ left: t.offsetLeft - tira.current.clientWidth / 2 + t.clientWidth / 2, behavior: "smooth" });
  }, [i]);
  if (!GRADING.length) return null;
  return (
    <section className="seccion seccion-grading" id="color">
      <div className="contenedor">
        <Rv><Ceja>{t("Color grading")}</Ceja></Rv>
        <Rv as="h2" delay={80} className="titulo titulo-grande rv-titulo">{t("El color también cuenta la historia.")}</Rv>
        <p className="lead">{t("Stills de cortos que filmamos y coloreamos nosotros. El mismo cuidado va en cada anuncio.")}</p>
        <Expandido lista={GRADING} i={i} setI={setI} pie={(f) => f.film} ancho />
      </div>
      <div className="grading" ref={tira}>
        {GRADING.map((f, k) => (
          <button key={f.src} className={`still${k === i ? " activa" : ""}`} onClick={() => setI(k === i ? null : k)}
            aria-label={`${t("Ver still de")} ${f.film}`} aria-pressed={k === i}>
            <img src={mini(f.src)} alt={`Still · ${f.film}`} loading="lazy" />
            <span className="still-film">{f.film}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

function Films() {
  return (
    <section className="seccion seccion-orn seccion-films" id="films">
      <Luz variante="c" proyector />
      <div className="contenedor">
        <Rv><Ceja>Nietzsche Films</Ceja></Rv>
        <Rv as="h2" delay={80} className="titulo titulo-mega rv-titulo">{t("Cine que hace sentir.")}</Rv>
        <Rv as="p" delay={160} className="lead">{t("No hacemos películas para entretener. Las hacemos para que sientas algo real. Cada fotograma existe para provocar una emoción honesta, de esas que se quedan contigo mucho después de que se apaga la pantalla.")}</Rv>
        <div className="films">
          {FILMS.map((f, i) => (
            <Rv as="article" key={f.titulo} delay={i * 120} className="film">
              <span className="film-estado">{t(f.estado)}</span>
              <h3>{t(f.titulo)}</h3>
              <p>{t(f.desc)}</p>
            </Rv>
          ))}
        </div>
        <blockquote className="films-cita">{t("“Una película se olvida. Una emoción, no.”")}</blockquote>
      </div>
    </section>
  );
}

function AlgoViene() {
  const [cd, setCd] = useState({ d: "000", h: "00", m: "00", s: "00" });
  useEffect(() => {
    const meta = new Date(CFG.estreno);
    const tick = () => {
      const diff = meta - new Date();
      if (diff <= 0) return;
      setCd({
        d: String(Math.floor(diff / 86400000)).padStart(3, "0"),
        h: String(Math.floor((diff % 86400000) / 3600000)).padStart(2, "0"),
        m: String(Math.floor((diff % 3600000) / 60000)).padStart(2, "0"),
        s: String(Math.floor((diff % 60000) / 1000)).padStart(2, "0"),
      });
    };
    tick(); const id = setInterval(tick, 1000); return () => clearInterval(id);
  }, []);

  return (
    <section className="seccion seccion-algo" id="events">
      <div className="contenedor algo-grid">
        <div>
          <Ceja>Events</Ceja>
          <h2 className="algo-titulo">Algo<br /><span className="glitch" data-texto="viene.">viene.</span></h2>
          <p className="algo-copy">{t("No sabemos si estás listo.")}<br />{t("No sabemos si importa.")}<br />{t("Pero ocurrirá.")}</p>
          <div className="cuenta" role="timer" aria-label={t("Cuenta regresiva al 1 de mayo de 2027")}>
            {[[cd.d, "días"], [cd.h, "horas"], [cd.m, "min"], [cd.s, "seg"]].map(([n, l]) => (
              <div key={l}><span className="cuenta-num">{n}</span><span className="cuenta-u">{t(l)}</span></div>
            ))}
          </div>
          <div className="cuenta-fecha">01 . 05 . 2027</div>
          <div className="algo-bloqueado"><Icono tipo="candado" size={18} />{t("Aún no es tiempo")}</div>
        </div>
        <div>
          {MUERTOS && <Calavera className="cal-algo" />}
          <h3 className="niveles-titulo">{enIngles() ? <><R>How much</R> do you want to know?</> : <><R>¿Cuánto</R> quieres saber?</>}</h3>
          <p className="niveles-sub">{t("Los accesos abren pronto. Accede según lo que estés dispuesto a averiguar.")}</p>
          <ul className="niveles">
            {NIVELES.map((n) => (
              <li key={n.n} className={n.top ? "nivel-top" : ""}>
                <div>
                  <span className="nivel-nombre">{t(n.n)}</span>
                  <span className="nivel-desc">{t(n.d)}</span>
                </div>
                <div className="nivel-precio">{n.p}<span>{t("USD al mes")}</span></div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function Filosofia() {
  return (
    <section className="seccion seccion-filo">
      <div className="contenedor">
        <Rv as="blockquote" className="filo-cita" lang="en">
          “He who has a <R>why</R> to live can bear almost any <R>how</R>.”
        </Rv>
        <p className="filo-autor">Friedrich Nietzsche</p>
      </div>
    </section>
  );
}

// ─── ALTAR DE MUERTOS DEL CINE 2026 ───
// Solo personas cuyo fallecimiento en 2026 está confirmado por fuentes (ver la lista con fuentes en el PR del altar).
// { nombre, rol, anios: "1940–2026", recuerdo }
const ALTAR = [
  { nombre: "Henner Hofmann", rol: "Director de fotografía, México", anios: "1950 a 2026", recuerdo: "Ariel por La leyenda de una máscara; dirigió el CCC" },
  { nombre: "Elsa Aguirre", rol: "Actriz, México", anios: "1930 a 2026", recuerdo: "Cine de Oro: Lluvia roja, Cuidado con el amor" },
  { nombre: "Ana Luisa Peluffo", rol: "Actriz, México", anios: "1929 a 2026", recuerdo: "Cine de Oro, más de 300 películas" },
  { nombre: "Gerardo Taracena", rol: "Actor, México", anios: "a los 55 años", recuerdo: "Apocalypto, Man on Fire, Sin nombre" },
  { nombre: "Alicia Caro", rol: "Actriz, Colombia y México", anios: "1930 a 2026", recuerdo: "Muchachas de uniforme, El Ceniciento" },
  { nombre: "Mario Cid", rol: "Actor y guionista, México", anios: "a los 93 años", recuerdo: "El águila negra contra los enmascarados de la muerte" },
  { nombre: "Otto Sirgo", rol: "Actor, Cuba y México", anios: "a los 79 años", recuerdo: "Rosa salvaje, Lazos de amor" },
  { nombre: "Béla Tarr", rol: "Director, Hungría", anios: "1955 a 2026", recuerdo: "Sátántangó, El caballo de Turín" },
  { nombre: "Robert Duvall", rol: "Actor, Estados Unidos", anios: "1931 a 2026", recuerdo: "El padrino, Apocalypse Now" },
  { nombre: "Catherine O'Hara", rol: "Actriz, Canadá", anios: "1954 a 2026", recuerdo: "Beetlejuice, Mi pobre angelito" },
  { nombre: "Tomàs Pladevall", rol: "Director de fotografía, España", anios: "1946 a 2026", recuerdo: "Tren de sombras; Gaudí de Honor" },
  { nombre: "Frederick Wiseman", rol: "Documentalista, Estados Unidos", anios: "1930 a 2026", recuerdo: "Titicut Follies; Óscar honorífico" },
  { nombre: "Nathalie Baye", rol: "Actriz, Francia", anios: "1948 a 2026", recuerdo: "Atrápame si puedes; cuatro César" },
  { nombre: "Luis Puenzo", rol: "Director, Argentina", anios: "1946 a 2026", recuerdo: "La historia oficial, primer Óscar para Argentina" },
  { nombre: "Dean Tavoularis", rol: "Diseñador de producción, Estados Unidos", anios: "1932 a 2026", recuerdo: "El padrino II, Apocalypse Now" },
  { nombre: "Marcia Lucas", rol: "Editora, Estados Unidos", anios: "1945 a 2026", recuerdo: "Star Wars, Taxi Driver" },
  { nombre: "Marjane Satrapi", rol: "Directora, Irán y Francia", anios: "1969 a 2026", recuerdo: "Persépolis, Pollo con ciruelas" },
  { nombre: "Sam Neill", rol: "Actor, Nueva Zelanda", anios: "1947 a 2026", recuerdo: "Jurassic Park, El piano" },
  { nombre: "Jean-Paul Rappeneau", rol: "Director, Francia", anios: "1932 a 2026", recuerdo: "Cyrano de Bergerac" },
  { nombre: "Patricio Guzmán", rol: "Documentalista, Chile", anios: "1941 a 2026", recuerdo: "La batalla de Chile, El botón de nácar" },
  { nombre: "Eva Marie Saint", rol: "Actriz, Estados Unidos", anios: "1924 a 2026", recuerdo: "Nido de ratas, Intriga internacional" },
];

function Altar() {
  if (!ALTAR.length) return null;
  const niveles = [ALTAR.slice(0, 3), ALTAR.slice(3, 9), ALTAR.slice(9)].filter((f) => f.length);
  return (
    <section className="seccion seccion-altar" id="altar">
      <PapelPicado n={22} className="pp-sec" />
      <div className="contenedor">
        <div className="altar-cabeza">
          <Rv><Ceja>{t("Altar de muertos 2026")}</Ceja></Rv>
          <Rv as="h2" delay={80} className="titulo titulo-grande rv-titulo">{t("A quienes nos dejaron historias.")}</Rv>
          <p className="lead">{t("Nuestra ofrenda a la gente del cine que se fue este año. Gracias por la luz que dejaron en la pantalla.")}</p>
        </div>
        <div className="altar">
          <ArcoFlores />
          {niveles.map((fila, i) => (
            <div className={`altar-nivel altar-nivel-${i + 1}`} key={i}>
              {fila.map((p) => <Retrato key={p.nombre} p={{ ...p, rol: t(p.rol), recuerdo: t(p.recuerdo),
                anios: enIngles() ? p.anios.replace(/^a los (\d+) años$/, "aged $1").replace(" a ", " to ") : p.anios }} />)}
            </div>
          ))}
          <Ofrenda n={17} />
        </div>
      </div>
    </section>
  );
}

// ─── PREGUNTAS FRECUENTES (viven en src/servicios.js: de ahí salen también las que lee Google) ───
function Preguntas() {
  const v = valores((n) => dinero(n) + (enPesos() ? " MXN" : ""));
  return (
    <section className="seccion seccion-preguntas" id="preguntas">
      <div className="contenedor preguntas-grid">
        <div>
          <Rv><Ceja>{t("Preguntas frecuentes")}</Ceja></Rv>
          <Rv as="h2" delay={80} className="titulo titulo-grande rv-titulo">{t("Lo que todos preguntan.")}</Rv>
        </div>
        <div className="preguntas">
          {PREGUNTAS.map((q) => (
            <details key={q.p} className="pregunta">
              <summary>{enIngles() ? q.pEn : q.p}</summary>
              <p>{llenar(enIngles() ? q.rEn : q.r, v)}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── CALAVERITA LITERARIA (solo en temporada de Muertos) ───
function Calaverita() {
  return (
    <section className="seccion seccion-calaverita" id="calaverita">
      <PapelPicado n={20} className="pp-sec" />
      <div className="contenedor calaverita-grid">
        <Calavera className="cal-calaverita" />
        <div>
          <Rv><Ceja>{t("Calaverita literaria")}</Ceja></Rv>
          <Rv as="h2" delay={80} className="titulo titulo-grande rv-titulo">{t("La Flaca llegó al estudio.")}</Rv>
          <div className="calaverita-verso">{(enIngles() ? CALAVERITA_EN : CALAVERITA).map((l) => <p key={l}>{l}</p>)}</div>
          <a className="btn btn-rojo" {...waProps("Hola, quiero cotizar un spot de Día de Muertos.")}>{t("Cotiza tu spot de temporada")}</a>
        </div>
      </div>
    </section>
  );
}

// ─── FORMULARIO (Web3Forms, misma llave y mismos campos de siempre) ───
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
// Las mismas opciones del cotizador; "Algo más" llega al HQ sin servicio (así el correo no dice "sobre algo más")
const OPCIONES = [...COT_TIPOS.map((x) => x.nombre), "Algo más"];

// Manda una solicitud por los dos caminos: al HQ (Supabase de Claqueta, entra a Ventas y avisa por Telegram)
// y el correo de respaldo (Web3Forms). Basta con que llegue por uno.
async function enviarSolicitud({ nombre, empresa = "", email, whatsapp = "", servicio = "", mensaje, asunto = "Cotización" }) {
  const aHQ = fetch(`${SB_URL}/rest/v1/solicitudes_web`, {
    method: "POST",
    headers: { apikey: CFG.supabase.llavePublica, "Content-Type": "application/json", Prefer: "return=minimal" },
    body: JSON.stringify([{
      nombre: nombre.slice(0, 120), empresa: empresa.slice(0, 160) || null, email: email.slice(0, 200),
      whatsapp: whatsapp.slice(0, 30) || null, servicio: servicio.slice(0, 80) || null, mensaje: mensaje.slice(0, 4000),
      pagina: (window.location.pathname + window.location.hash).slice(0, 200), origen: window.location.hostname.slice(0, 80),
    }]),
  }).then((r) => r.ok).catch(() => false);
  const aCorreo = (async () => {
    try {
      const r = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          access_key: CFG.web3forms,
          subject: `${asunto} — ${nombre}${empresa ? " · " + empresa : ""}`,
          from_name: "Doxa by Nietzsche — Sitio web",
          replyto: email,                               // responder desde Gmail contesta al cliente
          Nombre: nombre, Empresa: empresa || "—", Correo: email, Teléfono: whatsapp || "—",
          Servicio: servicio || "Sin especificar", Mensaje: mensaje,
          botcheck: "",                                 // campo anti-spam propio de Web3Forms
        }),
      });
      const j = await r.json().catch(() => ({}));
      return !!(r.ok && j.success);
    } catch { return false; }
  })();
  const [hq, correo] = await Promise.all([aHQ, aCorreo]);
  return hq || correo;
}

// "¿Empresa o artista pequeño?": que el precio no los deje sin un trabajo hecho a mano. Llega al HQ como solicitud normal.
function ProyectoPequeno({ volver }) {
  // Con pedidos en línea, la solicitud se manda con cuenta: así se le contesta y paga ahí mismo
  if (PEDIDOS) return (
    <div className="cot-recibo cot-otro cot-pequeno">
      <div className="cot-recibo-cab"><span>{t("Proyecto pequeño")}</span><button type="button" className="cot-volver" onClick={volver}>{t("Volver al cotizador")}</button></div>
      <h4 className="cot-otro-titulo">{t("¿Empresa o artista pequeño?")}</h4>
      <p>{t("No queremos que el precio te deje sin un trabajo hecho a mano. Cuéntanos quién eres y qué necesitas, y vemos si nos ajustamos a tu presupuesto.")}</p>
      <p>{t("Entra con tu cuenta de Google y manda tu solicitud: en Mis pedidos te decimos si nos ajustamos y, si sí, pagas ahí mismo. Si no alcanza, siempre te damos una propuesta.")}</p>
      <a className="btn btn-rojo btn-ancho" href="#propuesta">{t("Mandar mi solicitud")}</a>
    </div>
  );
  const [d, setD] = useState({ nombre: "", email: "", web: "", instagram: "", presupuesto: "", mensaje: "", website: "" });
  const [st, setSt] = useState("idle");
  const on = (k) => (e) => setD({ ...d, [k]: e.target.value });
  const enviar = async (e) => {
    e.preventDefault();
    if (st === "sending") return;
    if (d.website) { setSt("ok"); return; }             // honeypot
    const nombre = d.nombre.trim(), email = d.email.trim(), presupuesto = d.presupuesto.trim(), quien = d.mensaje.trim();
    if (!nombre || !email || !presupuesto || !quien) { setSt("missing"); return; }
    if (!EMAIL_RE.test(email)) { setSt("badEmail"); return; }
    setSt("sending");
    const mensaje = [
      "Empresa o artista pequeño: quiere ver si nos ajustamos a su presupuesto.",
      `Presupuesto para este trabajo: ${presupuesto}`,
      `Página web: ${d.web.trim() || "—"}`,
      `Instagram: ${d.instagram.trim() || "—"}`,
      "", "Quién es y qué necesita:", quien,
    ].join("\n");
    const ok = await enviarSolicitud({ nombre, email, mensaje, asunto: "Proyecto pequeño" });
    setSt(ok ? "ok" : "fail");
  };
  if (st === "ok") return (
    <div className="cot-recibo cot-otro cot-pequeno" role="status">
      <h4 className="cot-otro-titulo">{t("Recibido.")}</h4>
      <p>{t("Lo revisamos con calma y te escribimos en menos de 24 horas hábiles.")}</p>
      <button type="button" className="btn btn-linea btn-ancho" onClick={volver}>{t("Volver al cotizador")}</button>
    </div>
  );
  const errores = {
    missing: t("Faltan tu nombre, tu correo, tu presupuesto o qué necesitas."),
    badEmail: t("Ese correo no se ve válido. Revísalo."),
    fail: t("No se pudo enviar. Escríbenos directo a {correo}.", { correo: CFG.email }),
  };
  return (
    <form className="cot-recibo cot-otro cot-pequeno" onSubmit={enviar} noValidate>
      <div className="cot-recibo-cab"><span>{t("Proyecto pequeño")}</span><button type="button" className="cot-volver" onClick={volver}>{t("Volver al cotizador")}</button></div>
      <h4 className="cot-otro-titulo">{t("¿Empresa o artista pequeño?")}</h4>
      <p>{t("No queremos que el precio te deje sin un trabajo hecho a mano. Cuéntanos quién eres y qué necesitas, y vemos si nos ajustamos a tu presupuesto.")}</p>
      <div className="form-fila">
        <div className="campo"><label htmlFor="pp-nombre">{t("Nombre")}</label><input id="pp-nombre" value={d.nombre} onChange={on("nombre")} autoComplete="name" required /></div>
        <div className="campo"><label htmlFor="pp-email">{t("Correo")}</label><input id="pp-email" type="email" value={d.email} onChange={on("email")} autoComplete="email" required /></div>
      </div>
      <div className="form-fila">
        <div className="campo"><label htmlFor="pp-web">{t("Tu página web")} <span className="opcional">{t("(si tienes)")}</span></label><input id="pp-web" value={d.web} onChange={on("web")} inputMode="url" autoComplete="url" placeholder="tumarca.com" maxLength={200} /></div>
        <div className="campo"><label htmlFor="pp-ig">{t("Tu Instagram")} <span className="opcional">{t("(si tienes)")}</span></label><input id="pp-ig" value={d.instagram} onChange={on("instagram")} placeholder="@tumarca" maxLength={80} /></div>
      </div>
      <div className="campo"><label htmlFor="pp-presupuesto">{t("Tu presupuesto para este trabajo")}</label><input id="pp-presupuesto" value={d.presupuesto} onChange={on("presupuesto")} inputMode="decimal" placeholder={t("Ejemplo: $5,000")} maxLength={60} required /></div>
      <div className="campo"><label htmlFor="pp-mensaje">{t("Quién eres y qué necesitas")}</label><textarea id="pp-mensaje" rows={4} value={d.mensaje} onChange={on("mensaje")} maxLength={2500} required /></div>
      <input tabIndex={-1} autoComplete="off" aria-hidden="true" value={d.website} onChange={on("website")} className="miel" />
      {errores[st] && <p className="form-error" role="alert">{errores[st]}</p>}
      <button type="submit" className="btn btn-rojo btn-ancho" disabled={st === "sending"}>{st === "sending" ? t("Enviando…") : t("Enviar mi propuesta")}</button>
      <p className="form-aviso">{t("Usamos tus datos solo para contestarte y cotizar; no los vendemos ni los usamos para publicidad.")} <a href="#privacidad">{t("Aviso de privacidad")}</a>.</p>
    </form>
  );
}

function QuoteForm() {
  const [d, setD] = useState({ nombre: "", empresa: "", email: "", whatsapp: "", servicio: "", mensaje: "", website: "" });
  const [st, setSt] = useState("idle");
  const on = (k) => (e) => setD({ ...d, [k]: e.target.value });

  // El cotizador manda su presupuesto aquí para que el cliente solo agregue sus datos
  useEffect(() => {
    const alCotizar = (e) => setD((x) => ({ ...x, mensaje: e.detail.texto, servicio: COT_TIPOS.find((y) => y.id === e.detail.tipo)?.nombre || "Algo más" }));
    window.addEventListener("cotizacion", alCotizar);
    return () => window.removeEventListener("cotizacion", alCotizar);
  }, []);

  const send = async (e) => {
    e.preventDefault();
    if (st === "sending") return;                       // no doble envío
    if (d.website) { setSt("ok"); return; }             // honeypot: bot atrapado
    const nombre = d.nombre.trim(), email = d.email.trim(), mensaje = d.mensaje.trim();
    if (!nombre || !email || !mensaje) { setSt("missing"); return; }
    if (!EMAIL_RE.test(email)) { setSt("badEmail"); return; }

    setSt("sending");
    const ok = await enviarSolicitud({ nombre, empresa: d.empresa.trim(), email, whatsapp: d.whatsapp.trim(), servicio: d.servicio && d.servicio !== "Algo más" ? d.servicio : "", mensaje });
    setSt(ok ? "ok" : "fail");
  };

  if (st === "ok") return (
    <div className="form form-ok" role="status">
      <h3>{t("Solicitud recibida.")}</h3>
      <p>{t("Te respondemos en menos de 24 horas hábiles. Si es urgente, escríbenos directo a {correo}.", { correo: CFG.email })}</p>
    </div>
  );

  const errores = {
    missing: t("Faltan tu nombre, tu correo o la descripción del proyecto."),
    badEmail: t("Ese correo no se ve válido. Revísalo."),
    fail: t("No se pudo enviar. Escríbenos directo a {correo}.", { correo: CFG.email }),
  };

  return (
    <form className="form" onSubmit={send} noValidate>
      <div className="form-fila">
        <div className="campo"><label htmlFor="f-nombre">{t("Nombre")}</label>
          <input id="f-nombre" value={d.nombre} onChange={on("nombre")} autoComplete="name" required /></div>
        <div className="campo"><label htmlFor="f-empresa">{t("Empresa")} <span className="opcional">{t("(opcional)")}</span></label>
          <input id="f-empresa" value={d.empresa} onChange={on("empresa")} autoComplete="organization" /></div>
      </div>
      <div className="form-fila">
        <div className="campo"><label htmlFor="f-email">{t("Correo")}</label>
          <input id="f-email" type="email" value={d.email} onChange={on("email")} autoComplete="email" required /></div>
        <div className="campo"><label htmlFor="f-whatsapp">{t("Teléfono")} <span className="opcional">{t("(opcional)")}</span></label>
          <input id="f-whatsapp" type="tel" inputMode="tel" value={d.whatsapp} onChange={on("whatsapp")} autoComplete="tel" placeholder="55 1234 5678" /></div>
      </div>
      <div className="campo"><label htmlFor="f-servicio">{t("Qué necesitas")}</label>
        <select id="f-servicio" value={d.servicio} onChange={on("servicio")}>
          <option value="">{t("Elige un servicio")}</option>
          {OPCIONES.map((o) => <option key={o} value={o}>{t(o)}</option>)}
        </select></div>
      <div className="campo"><label htmlFor="f-mensaje">{t("Cuéntanos del proyecto")}</label>
        <textarea id="f-mensaje" rows={4} value={d.mensaje} onChange={on("mensaje")} required
          placeholder={t("Qué producto, para cuándo y presupuesto aproximado si ya lo tienes.")} /></div>
      {/* honeypot anti-spam — invisible para humanos */}
      <input tabIndex={-1} autoComplete="off" aria-hidden="true" value={d.website} onChange={on("website")} className="miel" />
      {errores[st] && <p className="form-error" role="alert">{errores[st]}</p>}
      <button type="submit" className="btn btn-rojo btn-ancho" disabled={st === "sending"}>
        {st === "sending" ? t("Enviando…") : t("Solicitar cotización")}
      </button>
      <p className="form-aviso">{t("Usamos tus datos solo para contestarte y cotizar; no los vendemos ni los usamos para publicidad.")} <a href="#privacidad">{t("Aviso de privacidad")}</a>.</p>
    </form>
  );
}

function Contacto() {
  return (
    <section className="seccion seccion-contacto" id="contacto">
      <div className="contenedor contacto-grid">
        <div>
          <Ceja>{t("Cotiza tu proyecto")}</Ceja>
          <h2 className="titulo titulo-grande">{t("El estudio está creciendo.")}</h2>
          <p className="lead">{t("Estás dentro de la visión, o la ves desde afuera. Cuéntanos qué necesitas y te contestamos por correo en menos de 24 horas hábiles con una propuesta y un precio.")}</p>
          <ul className="contacto-vias">
            {CFG.whatsapp && (
              <li><a {...waProps("Hola, quiero cotizar un proyecto.")}><Icono tipo="chat" size={22} />WhatsApp</a></li>
            )}
            <li><a href={`mailto:${CFG.email}?subject=${encodeURIComponent("Cotización — Nietzsche Studios")}`}>{CFG.email}</a></li>
            <li><a href={`https://ig.me/m/${CFG.instagram}`} target="_blank" rel="noopener noreferrer"><Icono tipo="chat" size={22} />{t("Mensaje directo en Instagram")}</a></li>
            <li className="contacto-lugar">{t("Ciudad de México")}</li>
          </ul>
        </div>
        <QuoteForm />
      </div>
    </section>
  );
}

// Idioma con un clic (ES ↔ EN) y moneda para ver los precios (siempre se cobra en MXN)
function SelectorIdioma() {
  const otro = enIngles() ? "es" : "en";
  return (
    <div className="idioma">
      <button type="button" className="idioma-btn" onClick={() => setIdioma(otro)}
        aria-label={enIngles() ? "ES, ver en español" : "EN, view in English"} lang={otro === "en" ? "en" : "es"}>
        {otro === "en" ? "EN" : "ES"}
      </button>
      <label className="idioma-moneda">
        <span className="visually-hidden">{t("Moneda")}</span>
        <select value={getMoneda()} onChange={(e) => { setMoneda(e.target.value); if (e.target.value !== "MXN") cargarTasas(); }} aria-label={t("Moneda")}>
          {MONEDAS.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </label>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  APP
// ═══════════════════════════════════════════════════════════════════
export default function App() {
  const [scrolled, setScrolled] = useState(false);
  const [barra, setBarra] = useState(false);
  const tcRef = useRef(null);
  // idioma y moneda: al cambiarlos se vuelve a pintar toda la página
  const [, setVersion] = useState(0);
  useEffect(() => {
    const otra = () => setVersion((v) => v + 1);
    window.addEventListener("ns-idioma", otra);
    document.documentElement.lang = enIngles() ? "en" : "es-MX";
    if (!enPesos()) cargarTasas();
    return () => window.removeEventListener("ns-idioma", otra);
  }, []);
  // Portal de pedidos en línea: #pedidos (mis pedidos), #pedir (pedido nuevo) o de regreso de Google (?entrar=1)
  const deHash = () => {
    const h = window.location.hash;
    if (h.startsWith("#privacidad")) return { vista: "privacidad" };
    if (h.startsWith("#terminos")) return { vista: "terminos" };
    if (!PEDIDOS) return null;
    if (h.startsWith("#pedir")) return { vista: "nuevo" };
    if (h.startsWith("#propuesta")) return { vista: "propuesta" };
    if (new URLSearchParams(window.location.search).has("entrar")) {   // de regreso de Google: a donde iba antes de entrar
      let v = null; try { v = sessionStorage.getItem("ns-vista"); sessionStorage.removeItem("ns-vista"); } catch {}
      return { vista: v === "propuesta" || v === "nuevo" ? v : "lista" };
    }
    if (h.startsWith("#pedidos")) return { vista: "lista" };
    return null;
  };
  const [portal, setPortal] = useState(deHash);
  useEffect(() => {
    const alHash = () => setPortal(deHash());
    const alPedir = (e) => { setPortal({ vista: "nuevo", inicial: e.detail }); window.history.replaceState(null, "", "#pedir"); };
    window.addEventListener("hashchange", alHash);
    window.addEventListener("pedir", alPedir);
    return () => { window.removeEventListener("hashchange", alHash); window.removeEventListener("pedir", alPedir); };
  }, []);
  const cerrarPortal = useCallback(() => { setPortal(null); window.history.replaceState(null, "", window.location.pathname); }, []);

  useEffect(() => {
    const raiz = document.documentElement;
    const quieto = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let ultimoY = window.scrollY, raf = 0, reposo;
    const z = (n) => String(n).padStart(2, "0");
    const h = () => {
      raf = 0;
      const y = window.scrollY;
      setScrolled(y > 40);
      const contacto = document.getElementById("contacto");
      const rc = contacto && contacto.getBoundingClientRect();
      const enContacto = rc && rc.top < window.innerHeight * 0.8 && rc.bottom > window.innerHeight * 0.2; // solo mientras el formulario está a la vista
      setBarra(y > window.innerHeight * 0.7 && !enContacto);

      // Línea de avance como la de una timeline de edición (la página "dura" 3 minutos)
      const max = Math.max(1, raiz.scrollHeight - window.innerHeight);
      const p = Math.min(1, y / max);
      raiz.style.setProperty("--avance", p.toFixed(4));
      if (tcRef.current) {
        const f = Math.round(p * 180 * 24);
        tcRef.current.textContent = `00:${z(Math.floor(f / 1440))}:${z(Math.floor(f / 24) % 60)}:${z(f % 24)}`;
      }

      // Las cintas de texto se inclinan con la velocidad del scroll
      if (!quieto) {
        const v = y - ultimoY;
        raiz.style.setProperty("--sesgo", `${Math.max(-12, Math.min(12, -v * 0.35)).toFixed(2)}deg`);
        clearTimeout(reposo);
        reposo = setTimeout(() => raiz.style.setProperty("--sesgo", "0deg"), 120);
      }
      ultimoY = y;
    };
    const on = () => { if (!raf) raf = requestAnimationFrame(h); };
    h();
    window.addEventListener("scroll", on, { passive: true });
    return () => { window.removeEventListener("scroll", on); cancelAnimationFrame(raf); clearTimeout(reposo); };
  }, []);

  return (
    <div className={`sitio${TEMPORADA ? ` tema-${TEMPORADA.id}` : ""}`}>
      {TEMPORADA && (<>
        <div className="niebla" aria-hidden="true"><i /><i /></div>
        {MUERTOS && <><Petalos /><Murcielagos /></>}
        <div className="apagon" aria-hidden="true" />
      </>)}
      <a className="saltar" href="#manifiesto">{t("Saltar al contenido")}</a>
      <nav className={`nav${scrolled ? " nav-fija" : ""}`} aria-label="Principal">
        <a className="logo" href="#inicio" aria-label="Nietzsche Studios, inicio">
          <Ouroboros size={26} /><span>Nietzsche Studios</span>
        </a>
        <div className="nav-links">
          {HAY_TRABAJO && <a href="#trabajo">{t("Trabajo")}</a>}
          <a href="#doxa">{t("Servicios")}</a>
          <a href="#precios">{t("Precios")}</a>
          <a href="#films">Films</a>
          <a href="#events">Events</a>
        </div>
        <SelectorIdioma />
        {PEDIDOS && <a className="nav-cuenta" href="#pedidos" aria-label={t("Mis pedidos")}>
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><circle cx="12" cy="8" r="4" fill="none" stroke="currentColor" strokeWidth="1.8" /><path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
          <span>{t("Mis pedidos")}</span>
        </a>}
        <a className="btn btn-rojo btn-chico" {...waProps("Hola, vi su página y quiero cotizar.")}>{t("Cotizar")}</a>
        <div className="avance" aria-hidden="true"><i /><span ref={tcRef} className="avance-tc">00:00:00:00</span></div>
      </nav>

      <main>
        {/* Primero lo que el cliente viene a ver (trabajo), luego lo comercial y hasta abajo la marca y la filosofía */}
        <Hero />
        <Trabajo />
        <Fotografia />
        <ColorGrading />
        <Marquesina key={`m1${getIdioma()}`} frases={TEMPORADA ? TEMPORADA.cinta : ["Spots", "Video de marca", "Contenido", "Fotografía", "Animación 3D", "Cine"]} />
        <Doxa />
        <ElSet />
        <Precios />
        <Testimonios />
        <Preguntas />
        <Contacto />
        {MUERTOS && <Altar />}
        {MUERTOS && <Calaverita />}
        <Manifiesto />
        <Pilares />
        <Films />
        <AlgoViene />
        <Filosofia />
        <Marquesina key={`m2${getIdioma()}`} linea frases={["Hecho a mano", "Por humanos", "Para humanos"]} />
      </main>

      <div className="pie-gigante" aria-hidden="true"><span>Nietzsche</span>{MUERTOS && <Ofrenda n={13} />}</div>
      <footer className="pie">
        <div className="pie-marca"><Ouroboros size={22} /><span>Nietzsche Studios × CDMX</span></div>
        <div className="pie-lema"><R>{t("Excelencia")}</R> {t("no es un acto. Es un hábito.")}</div>
        <div className="pie-links">
          <a href={`https://instagram.com/${CFG.instagram}`} target="_blank" rel="noopener noreferrer">Instagram</a>
          <a href={`mailto:${CFG.email}`}>{t("Correo")}</a>
          <a href={CFG.patreon} target="_blank" rel="noopener noreferrer">Patreon</a>
          <a href="#terminos">{t("Términos y condiciones")}</a>
          <a href="#privacidad">{t("Privacidad y cookies")}</a>
          <span>By Jamez — MMXXVI</span>
        </div>
        {/* datos que la ley pide a la vista para vender en línea (se llenan en src/negocio.js) */}
        <p className="pie-datos">{[NEGOCIO.titular, NEGOCIO.domicilio, NEGOCIO.telefono && `Tel. ${NEGOCIO.telefono}`, `${t("Atención:")} ${enIngles() ? NEGOCIO.horarioEn : NEGOCIO.horario}`].filter(Boolean).join(" · ")}</p>
      </footer>

      <Suspense fallback={null}>
        {portal?.vista === "privacidad" && <AvisoPrivacidad cerrar={cerrarPortal} />}
        {portal?.vista === "terminos" && <Terminos cerrar={cerrarPortal} />}
        {portal && !["privacidad", "terminos"].includes(portal.vista) && <Portal sb={SB_URL} llave={CFG.supabase.llavePublica} stripeLlave={CFG.stripe.llavePublica} vista={portal.vista} inicial={portal.inicial} cerrar={cerrarPortal} />}
      </Suspense>
      <a className={`barra-movil${barra ? " visible" : ""}`} {...waProps("Hola, vi su página y quiero cotizar.")}
        aria-hidden={!barra} tabIndex={barra ? 0 : -1}>
        <Icono tipo="chat" size={20} />{t(CTA_WA)}
      </a>
    </div>
  );
}
